// admin/src/lib/image.ts
//
// Client-side image compression before upload (Supabase free tier: 1 GB
// storage, and every app view costs egress). Each image is scaled to the
// smallest size that still covers the largest box the app draws it in at
// 3x (iPhone Pro Max), then encoded as WebP (JPEG where the browser can't
// encode WebP), stepping quality and size down until it fits a byte
// budget. The pure math is tested; the canvas glue runs in the browser.

export type Dimensions = { width: number; height: number };

/** The device-pixel box an image must cover, and the most bytes it may take. */
export type ImageTarget = { coverWidth: number; coverHeight: number; maxBytes: number };

export const IMAGE_TARGETS = {
  /** Exercise hero carousel: (440 - 2*20) pt wide, 0.86 of that tall, @3x. Also video posters. */
  hero: { coverWidth: 1200, coverHeight: 1032, maxBytes: 250_000 },
  /** Train hero and My Workouts cover cards (focal-point cropped). */
  cover: { coverWidth: 1200, coverHeight: 1000, maxBytes: 250_000 },
  /** Equipment chips: 56 pt on the exercise page, 160 px on this site. */
  equipment: { coverWidth: 400, coverHeight: 400, maxBytes: 60_000 },
} as const satisfies Record<string, ImageTarget>;

export const QUALITY_STEPS = [0.82, 0.74, 0.66, 0.58] as const;
const SHRINK_STEP = 0.85;
const MAX_SHRINKS = 4;

/**
 * The smallest size (never upscaled) at which `source` still covers
 * `coverWidth` x `coverHeight` when drawn with object-fit: cover.
 */
export function computeCoverDimensions({ width, height }: Dimensions, { coverWidth, coverHeight }: Pick<ImageTarget, 'coverWidth' | 'coverHeight'>): Dimensions {
  if (width <= 0 || height <= 0) return { width: 1, height: 1 };
  const scale = Math.min(1, Math.max(coverWidth / width, coverHeight / height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** Scales both sides by `factor`, keeping at least 1x1. */
export function shrink({ width, height }: Dimensions, factor: number = SHRINK_STEP): Dimensions {
  return { width: Math.max(1, Math.round(width * factor)), height: Math.max(1, Math.round(height * factor)) };
}

/** "4.2 MB", "184 KB", "900 B" -- decimal units, matching what Finder shows. */
export function formatBytes(bytes: number): string {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  if (bytes >= 1_000) return `${Math.round(bytes / 1_000)} KB`;
  return `${bytes} B`;
}

export type CompressedImage = {
  blob: Blob;
  mime: 'image/webp' | 'image/jpeg';
  ext: 'webp' | 'jpg';
  width: number;
  height: number;
  originalBytes: number;
};

function encode(canvas: HTMLCanvasElement, mime: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Could not encode the image'))), mime, quality),
  );
}

/** Compresses an image File/Blob to fit `target`. Browser-only. */
export async function compressImage(file: Blob, target: ImageTarget): Promise<CompressedImage> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error("Can't read this image. Use JPEG, PNG or WebP.");
  }
  try {
    let dims = computeCoverDimensions({ width: bitmap.width, height: bitmap.height }, target);
    let mime: CompressedImage['mime'] = 'image/webp';
    let best: { blob: Blob; dims: Dimensions } | null = null;

    for (let attempt = 0; attempt <= MAX_SHRINKS; attempt++) {
      const canvas = document.createElement('canvas');
      canvas.width = dims.width;
      canvas.height = dims.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('2D canvas context unavailable');
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(bitmap, 0, 0, dims.width, dims.height);

      for (const quality of QUALITY_STEPS) {
        let blob = await encode(canvas, mime, quality);
        // Safari can't encode WebP and silently hands back a PNG.
        if (mime === 'image/webp' && blob.type !== 'image/webp') {
          mime = 'image/jpeg';
          blob = await encode(canvas, mime, quality);
        }
        if (!best || blob.size < best.blob.size) best = { blob, dims };
        if (blob.size <= target.maxBytes) {
          return { blob, mime, ext: mime === 'image/webp' ? 'webp' : 'jpg', ...dims, originalBytes: file.size };
        }
      }
      dims = shrink(dims);
    }

    // Unreachable in practice (4 shrinks at q0.58 is tiny); ship the smallest attempt.
    return { blob: best!.blob, mime, ext: mime === 'image/webp' ? 'webp' : 'jpg', ...best!.dims, originalBytes: file.size };
  } finally {
    bitmap.close();
  }
}
